// Mitigations and the decision, and the log of how it has moved.
//
// While Draft the fields are a form: "Save draft" keeps them (the reasoning can
// be left for later) and "Record decision" saves them and pins the forecasts on
// screen as the evidence — the first entry in the decision log, and the one
// point the reasoning is required. Once decided, the decision is read-only and
// moves on by *updates*: each says how things have gone since (much the same,
// better, worse, mixed), restates the decision and mitigations as they now
// stand, and pins the forecasts it was made on. Reopening is only for changing
// the points or the window; the log records it and keeps everything before.
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, FormControlLabel, FormGroup, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import AddCommentIcon from "@mui/icons-material/AddComment";
import { notify } from "../notify";
import { useFullScreenDialog } from "../useFullScreenDialog";
import SafetySection from "./SafetySection";
import RunCommentary from "./RunCommentary";
import { SeverityChip } from "./ForecastsSection";
import { SAFETY_API, fmtDateTime } from "./format";

const fromAssessment = (a) => ({
  mitigations: new Set((a.mitigations || []).map(m => m.code)),
  mitigation_notes: a.mitigation_notes || "",
  decision: a.decision || "",
  rationale: a.rationale || "",
});

const lastDecision = (a) => [...(a.decisions || [])].reverse().find(e => e.kind !== "reopened");

export default function DecisionPanel({ assessment: a, config, onChange }) {
  const decided = a.status === "Decided";
  const [form, setForm] = useState(() => fromAssessment(a));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [reopening, setReopening] = useState(false);

  // Take the server's copy whenever a different record (or a new version of this
  // one) arrives — but never over unsaved typing.
  useEffect(() => {
    if (!dirty) setForm(fromAssessment(a));
  }, [a.id, a.updated_at, a.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const edit = (patch) => { setForm(f => ({ ...f, ...patch })); setDirty(true); };
  const body = () => ({
    mitigations: [...form.mitigations],
    mitigation_notes: form.mitigation_notes,
    decision: form.decision,
    rationale: form.rationale,
  });
  const save = async () => {
    const res = await axios.put(`${SAFETY_API}/assessments/${a.id}`, body());
    setDirty(false);
    onChange(res.data);
    return res.data;
  };
  const run = (fn) => { setBusy(true); fn().catch(() => {}).finally(() => setBusy(false)); };

  const saveDraft = () => run(async () => { await save(); notify("Draft saved.", "success"); });
  const record = () => run(async () => {
    await save();
    const res = await axios.post(`${SAFETY_API}/assessments/${a.id}/decide`);
    onChange(res.data);
    notify("Decision recorded.", "success");
  });

  const fetchedCount = a.forecasts.filter(f => !f.error).length;
  const canRecord = form.decision && form.rationale.trim() && fetchedCount > 0;
  const missing = [
    !form.decision && "choose a decision",
    !form.rationale.trim() && "write the reasoning",
    !fetchedCount && "fetch forecasts",
  ].filter(Boolean);
  const hadDecision = !!lastDecision(a);
  const last = lastDecision(a);

  return (
    <SafetySection id="decision" title="Mitigations and decision"
      summary={<DecisionSummary assessment={a} config={config} />}>
      {decided && (
        <Alert severity="success" sx={{ mb: 2 }} action={
          <Stack direction="row" spacing={1}>
            <Button color="inherit" size="small" startIcon={<AddCommentIcon />} onClick={() => setUpdating(true)}>
              Add update
            </Button>
            <Button color="inherit" size="small" onClick={() => setReopening(true)} disabled={busy}>Reopen</Button>
          </Stack>}>
          {last?.kind === "update" ? "Last updated" : "Decided"} by {a.decided_by_name || a.decided_by_email} on{" "}
          {fmtDateTime(a.decided_at)}
          {last?.run_fetched_at ? `, on the forecast fetched ${fmtDateTime(last.run_fetched_at)}` : ""}.
        </Alert>
      )}
      {decided && a.since_decision && (
        <RunCommentary comparison={a.since_decision} config={config} sx={{ mb: 2 }}
                       title="Since the decision was made" />
      )}

      {decided ? (
        <CurrentDecision assessment={a} config={config} />
      ) : (
        <Stack spacing={2}>
          <MitigationPicker config={config} value={form.mitigations} onChange={(v) => edit({ mitigations: v })} />

          <TextField label="Mitigation details" multiline minRows={2} value={form.mitigation_notes}
                     onChange={e => edit({ mitigation_notes: e.target.value })}
                     placeholder="Which bad-weather route, where the extra cover goes, what the kit list adds…" />

          <TextField select label="Decision" value={form.decision} sx={{ maxWidth: 360 }}
                     onChange={e => edit({ decision: e.target.value })}>
            <MenuItem value=""><em>Not decided</em></MenuItem>
            {config.decisions.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </TextField>

          <TextField label="Reasoning" multiline minRows={4} value={form.rationale}
                     onChange={e => edit({ rationale: e.target.value })}
                     helperText="What in the forecasts led to this decision — the record of why. It can wait while this is a draft; it's needed to record the decision." />

          <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
            <Button onClick={saveDraft} disabled={busy || !dirty}>Save draft</Button>
            <Button variant="contained" onClick={record} disabled={busy || !canRecord}>
              {hadDecision ? "Record revised decision" : "Record decision"}
            </Button>
            {missing.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                To record it, {missing.join(", ").replace(/, ([^,]*)$/, " and $1")}.
              </Typography>
            )}
          </Stack>
        </Stack>
      )}

      <UpdateDialog open={updating} assessment={a} config={config} onClose={() => setUpdating(false)}
                    onSaved={(next) => { setUpdating(false); onChange(next); notify("Update recorded.", "success"); }} />
      <ReopenDialog open={reopening} assessment={a} onClose={() => setReopening(false)}
                    onSaved={(next) => { setReopening(false); setDirty(false); onChange(next); }} />
    </SafetySection>
  );
}

// The checkbox catalogue, in its groups.
function MitigationPicker({ config, value, onChange }) {
  const toggle = (code) => {
    const next = new Set(value);
    if (next.has(code)) next.delete(code); else next.add(code);
    onChange(next);
  };
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2 }}>
      {config.mitigations.map(g => (
        <Box key={g.group}>
          <Typography variant="subtitle2">{g.group}</Typography>
          <FormGroup>
            {g.items.map(m => (
              <FormControlLabel key={m.code}
                control={<Checkbox size="small" checked={value.has(m.code)} onChange={() => toggle(m.code)} />}
                label={<Typography variant="body2">{m.label}</Typography>} />
            ))}
          </FormGroup>
        </Box>
      ))}
    </Box>
  );
}

// Only what is ticked, under its group. Stored labels are used, so a mitigation
// since reworded or dropped from the catalogue still reads as it was chosen.
function TickedMitigations({ config, mitigations }) {
  const list = mitigations || [];
  if (!list.length) return <Typography variant="body2" color="text.secondary">No mitigations ticked.</Typography>;
  const groupOf = {};
  for (const g of config.mitigations) for (const m of g.items) groupOf[m.code] = g.group;
  const groups = [];
  for (const m of list) {
    const g = groupOf[m.code] || "Other";
    let entry = groups.find(x => x.group === g);
    if (!entry) groups.push(entry = { group: g, items: [] });
    entry.items.push(m);
  }
  return (
    <Stack spacing={0.5}>
      {groups.map(g => (
        <Box key={g.group}>
          <Typography variant="caption" color="text.secondary">{g.group}</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {g.items.map(m => <Typography component="li" variant="body2" key={m.code}>{m.label}</Typography>)}
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

function CurrentDecision({ assessment: a, config }) {
  return (
    <Stack spacing={1.5}>
      <Box>
        <Typography variant="caption" color="text.secondary">Decision</Typography>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>{a.decision || "—"}</Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">Mitigations</Typography>
        <TickedMitigations config={config} mitigations={a.mitigations} />
      </Box>
      {a.mitigation_notes && (
        <Box>
          <Typography variant="caption" color="text.secondary">Mitigation details</Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>{a.mitigation_notes}</Typography>
        </Box>
      )}
      {a.rationale && (
        <Box>
          <Typography variant="caption" color="text.secondary">Latest reasoning</Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>{a.rationale}</Typography>
        </Box>
      )}
    </Stack>
  );
}

function DecisionSummary({ assessment: a, config }) {
  const decided = a.status === "Decided";
  return (
    <Stack spacing={1}>
      <Typography variant="body2">
        <Box component="span" sx={{ fontWeight: 600 }}>{a.decision || "Not decided"}</Box>
        <Typography component="span" variant="body2" color="text.secondary">
          {decided ? ` · ${fmtDateTime(a.decided_at)} by ${a.decided_by_name || a.decided_by_email}` : " · draft"}
        </Typography>
      </Typography>
      <TickedMitigations config={config} mitigations={a.mitigations} />
    </Stack>
  );
}

function UpdateDialog({ open, assessment: a, config, onClose, onSaved }) {
  const fullScreen = useFullScreenDialog();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const verdict = a.since_decision?.verdict;
    setForm({
      trend: config.verdict_trend?.[verdict] || config.trends[0],
      decision: a.decision || "",
      mitigations: new Set((a.mitigations || []).map(m => m.code)),
      mitigation_notes: a.mitigation_notes || "",
      rationale: "",
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!form) return null;
  const set = (patch) => setForm(f => ({ ...f, ...patch }));
  const valid = form.trend && form.decision && form.rationale.trim();
  const save = () => {
    setSaving(true);
    axios.post(`${SAFETY_API}/assessments/${a.id}/decision-updates`, {
      trend: form.trend, decision: form.decision, mitigations: [...form.mitigations],
      mitigation_notes: form.mitigation_notes, rationale: form.rationale,
    }).then(res => onSaved(res.data)).catch(() => {}).finally(() => setSaving(false));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle>Update the decision</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Record how things look now. The update is made on the forecasts as they now stand, and
            joins the decision log; nothing earlier is changed. Leave the decision and mitigations as
            they are if they still stand.
          </Typography>
          {a.since_decision && <RunCommentary comparison={a.since_decision} config={config} title="Since the decision was made" />}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField select label="Since the last entry, things are" value={form.trend} sx={{ minWidth: 240 }}
                       onChange={e => set({ trend: e.target.value })}
                       helperText={a.since_decision ? "Suggested from the forecast comparison." : undefined}>
              {config.trends.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField select label="Decision" value={form.decision} sx={{ minWidth: 280 }}
                       onChange={e => set({ decision: e.target.value })}>
              {config.decisions.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </TextField>
          </Stack>
          <MitigationPicker config={config} value={form.mitigations} onChange={(v) => set({ mitigations: v })} />
          <TextField label="Mitigation details" multiline minRows={2} value={form.mitigation_notes}
                     onChange={e => set({ mitigation_notes: e.target.value })} />
          <TextField label="What has changed, and why the decision stands or changes" multiline minRows={3}
                     value={form.rationale} required autoFocus
                     onChange={e => set({ rationale: e.target.value })} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={!valid || saving}>Record update</Button>
      </DialogActions>
    </Dialog>
  );
}

function ReopenDialog({ open, assessment: a, onClose, onSaved }) {
  const [note, setNote] = useState("");
  useEffect(() => { if (open) setNote(""); }, [open]);
  const save = () => {
    axios.post(`${SAFETY_API}/assessments/${a.id}/reopen`, { note })
      .then(res => onSaved(res.data)).catch(() => {});
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Reopen the assessment?</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Reopening is for changing the points or the window. To record that the forecast has moved,
          or that the decision or mitigations have changed, use <strong>Add update</strong> instead —
          it doesn't need reopening. The decision log keeps everything recorded so far.
        </DialogContentText>
        <TextField label="Why (optional)" fullWidth multiline minRows={2} value={note}
                   onChange={e => setNote(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save}>Reopen</Button>
      </DialogActions>
    </Dialog>
  );
}

// --- The decision log ---------------------------------------------------------

const KIND_LABEL = { initial: "Decision", update: "Update", revised: "Revised decision", reopened: "Reopened" };
const TREND_CHIP = { "Much the same": "default", Better: "success", Worse: "warning", Mixed: "warning" };

export function DecisionHistory({ assessment: a, config, onViewRun }) {
  const entries = [...(a.decisions || [])].reverse();  // newest first
  if (!entries.length) return null;
  const chronological = a.decisions;

  return (
    <SafetySection id="decision-history" title={`Decision history (${entries.length})`}
      subtitle="Every recorded decision and update, newest first, with the forecast each was made on."
      summary={<Typography variant="body2" color="text.secondary">
        {entries.length} entr{entries.length === 1 ? "y" : "ies"}; the latest {fmtDateTime(entries[0].created_at)}
        {entries[0].author_name ? ` by ${entries[0].author_name}` : ""}.
      </Typography>}>
      <Stack spacing={0}>
        {entries.map((e, i) => {
          const prev = [...chronological.slice(0, chronological.indexOf(e))].reverse().find(x => x.kind !== "reopened");
          return (
            <Box key={e.id} sx={{ borderLeft: "3px solid", borderColor: e.kind === "reopened" ? "divider" : "primary.main",
                                  pl: 2, py: 1.5, ...(i > 0 && { borderTop: "1px solid", borderTopColor: "divider" }) }}>
              <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
                <Typography variant="subtitle2">{KIND_LABEL[e.kind] || e.kind}</Typography>
                {e.trend && <Chip size="small" label={e.trend} color={TREND_CHIP[e.trend] || "default"} variant="outlined" />}
                {e.kind !== "reopened" && <SeverityChip severity={e.severity} prefix="Forecast: " />}
                <Typography variant="body2" color="text.secondary">
                  {fmtDateTime(e.created_at)}{e.author_name ? ` · ${e.author_name}` : ""}
                </Typography>
              </Stack>
              {e.kind !== "reopened" && (
                <EntryBody entry={e} prev={prev} config={config} onViewRun={onViewRun} runs={a.runs} />
              )}
              {e.kind === "reopened" && e.rationale && (
                <Typography variant="body2" sx={{ whiteSpace: "pre-line", mt: 0.5 }}>{e.rationale}</Typography>
              )}
            </Box>
          );
        })}
      </Stack>
    </SafetySection>
  );
}

function EntryBody({ entry: e, prev, config, onViewRun, runs }) {
  const codes = (list) => new Set((list || []).map(m => m.code));
  const before = prev ? codes(prev.mitigations) : null;
  const now = codes(e.mitigations);
  const added = (e.mitigations || []).filter(m => before && !before.has(m.code));
  const removed = (prev?.mitigations || []).filter(m => !now.has(m.code));
  const decisionChanged = prev && prev.decision !== e.decision;
  const newestRun = runs?.[0]?.id;

  return (
    <Stack spacing={0.75} sx={{ mt: 0.75 }}>
      <Typography variant="body2">
        <Box component="span" sx={{ fontWeight: 600 }}>{e.decision}</Box>
        {decisionChanged && (
          <Typography component="span" variant="body2" color="text.secondary"> — changed from {prev.decision}</Typography>
        )}
        {prev && !decisionChanged && (
          <Typography component="span" variant="body2" color="text.secondary"> — unchanged</Typography>
        )}
      </Typography>
      {!prev ? (
        <TickedMitigations config={config} mitigations={e.mitigations} />
      ) : added.length || removed.length ? (
        <Box>
          {added.length > 0 && <Typography variant="body2">Mitigations added: {added.map(m => m.label).join("; ")}.</Typography>}
          {removed.length > 0 && <Typography variant="body2">Mitigations removed: {removed.map(m => m.label).join("; ")}.</Typography>}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Mitigations unchanged ({(e.mitigations || []).length}).
        </Typography>
      )}
      {e.mitigation_notes && (!prev || prev.mitigation_notes !== e.mitigation_notes) && (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
          Details: {e.mitigation_notes}
        </Typography>
      )}
      {e.rationale && <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>{e.rationale}</Typography>}
      {e.run_id && e.run_fetched_at && (
        <Box>
          <Button size="small" onClick={() => onViewRun(e.run_id === newestRun ? null : e.run_id)}>
            View the forecast it was made on ({fmtDateTime(e.run_fetched_at)})
          </Button>
        </Box>
      )}
    </Stack>
  );
}
